// Event wall-clock bounds must match backend (`backendnew/utils/eventDateTime.js`) so
// "expired", registration open/close, and capacity UI agree with API validation.
// Set REACT_APP_EVENT_TZ_OFFSET_MINUTES at build time to match backend EVENT_TZ_OFFSET_MINUTES (default +330 IST).

const DEFAULT_TZ_OFFSET_MINUTES = 330;

function normalizeTzOffsetMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_TZ_OFFSET_MINUTES;
  if (n < -720) return -720;
  if (n > 840) return 840;
  return Math.trunc(n);
}

function getEventTzOffsetMinutes() {
  const raw = process.env.REACT_APP_EVENT_TZ_OFFSET_MINUTES;
  return normalizeTzOffsetMinutes(raw);
}

function parseTime(timeString) {
  if (typeof timeString !== 'string') return null;
  const trimmed = timeString.trim();
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/.exec(trimmed);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const second = m[3] == null ? 0 : Number(m[3]);
  const meridiem = m[4] ? m[4].toUpperCase() : null;
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) return null;
  if (minute < 0 || minute > 59) return null;
  if (second < 0 || second > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'AM') {
      hour = hour === 12 ? 0 : hour;
    } else {
      hour = hour === 12 ? 12 : hour + 12;
    }
  } else if (hour < 0 || hour > 23) {
    return null;
  }
  return { hour, minute, second };
}

function ymdFromValue(dateValue) {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    if (Number.isNaN(dateValue.getTime())) return null;
    return {
      year: dateValue.getUTCFullYear(),
      month: dateValue.getUTCMonth() + 1,
      day: dateValue.getUTCDate()
    };
  }

  if (typeof dateValue === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue.trim());
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    return { year, month, day };
  }

  return null;
}

function makeInstantFromLocal(ymd, time, kind, tzOffsetMinutes) {
  if (!ymd) return null;

  let hour = 0;
  let minute = 0;
  let second = 0;
  let ms = 0;

  if (time) {
    hour = time.hour;
    minute = time.minute;
    second = time.second;
  } else if (kind === 'end') {
    hour = 23;
    minute = 59;
    second = 59;
    ms = 999;
  }

  const utcMillis = Date.UTC(ymd.year, ymd.month - 1, ymd.day, hour, minute, second, ms) - (tzOffsetMinutes * 60 * 1000);
  const d = new Date(utcMillis);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getEventStartEnd(event) {
  if (!event) return { start: null, end: null };

  const tzOffsetMinutes = getEventTzOffsetMinutes();

  const startYmd = ymdFromValue(event.startDate || event.date || null);
  const endYmd = ymdFromValue(event.endDate || event.startDate || event.date || null);

  const startTime = parseTime(event.startTime || event.time || '');
  const endTime = parseTime(event.endTime || '');

  const start = makeInstantFromLocal(startYmd, startTime, 'start', tzOffsetMinutes);
  const end = makeInstantFromLocal(endYmd, endTime, 'end', tzOffsetMinutes);

  return { start, end };
}

export function isEventExpired(event, now = new Date()) {
  const { end } = getEventStartEnd(event);
  if (!end) return false;
  return now >= end;
}

function toInstant(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function getEventRegistrationStartEnd(event) {
  if (!event) return { start: null, end: null };
  const start = toInstant(event.eventRegistrationStartDateTime);
  const end = toInstant(event.eventRegistrationEndDateTime);
  return { start, end };
}

export function getEventRegistrationState(event, now = new Date()) {
  const { start, end } = getEventRegistrationStartEnd(event);
  if (start && end && start > end) {
    return { state: 'misconfigured', start, end };
  }
  if (start && now < start) {
    return { state: 'not_open', start, end };
  }
  if (end && now > end) {
    return { state: 'closed', start, end };
  }
  return { state: 'open', start, end };
}
